import React, { FC, useCallback, useRef, useState } from 'react';
import { Button, Drawer, Tooltip } from 'antd';
import { useTranslate } from '@/translations';
import { categoryLabel } from '@/translations/categoryLabel';
import { timeConverter } from '@/utils/helpers';
import {
  Icon,
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
  faExpand,
  faLocationDot,
} from '../../Icon';
import {
  FullscreenMap,
  Map,
  MapFullscreenData,
  type MapHandle,
} from '../../Map';
import type { LastScanModel } from '@/types';

export interface LocationCardProps {
  entries: LastScanModel[];
  tabKind: 'located' | 'lastScan';
  enableScroll: boolean;
  /** Optional external ref (e.g. PDF snapshot). Falls back to an internal ref. */
  mapRef?: React.Ref<MapHandle>;
  mapImageForPdf?: string | null;
}

const Stat: FC<{ label: string; value: string | number }> = ({
  label,
  value,
}) => (
  <div
    className="d-flex align-items-baseline"
    style={{
      gap: 4,
      padding: '2px 6px',
      background: '#fff',
      border: '1px solid #f0f0f0',
      borderRadius: 4,
      fontSize: 11,
      lineHeight: 1.4,
      whiteSpace: 'nowrap',
      minWidth: 0,
      overflow: 'hidden',
    }}
  >
    <span
      style={{
        color: '#8c8c8c',
        fontWeight: 600,
        letterSpacing: 0.4,
        flexShrink: 0,
      }}
    >
      {label}
    </span>
    <span
      style={{
        color: '#262626',
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
      }}
      title={String(value)}
    >
      {value}
    </span>
  </div>
);

export const LocationCard: FC<LocationCardProps> = ({
  entries,
  tabKind,
  enableScroll,
  mapRef: externalMapRef,
  mapImageForPdf,
}) => {
  const { t } = useTranslate();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showFullMap, setShowFullMap] = useState(false);
  const [mapViewData, setMapViewData] = useState<MapFullscreenData>();
  const internalMapRef = useRef<MapHandle | null>(null);

  const setMapRef = useCallback(
    (instance: MapHandle | null) => {
      internalMapRef.current = instance;
      if (!externalMapRef) return;
      if (typeof externalMapRef === 'function') {
        externalMapRef(instance);
      } else {
        (externalMapRef as React.MutableRefObject<MapHandle | null>).current =
          instance;
      }
    },
    [externalMapRef],
  );

  if (entries.length === 0) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{
          height: 300,
          gap: 6,
          background: '#fafafa',
          border: '1px dashed #e0e0e0',
          borderRadius: 8,
          color: '#8c8c8c',
        }}
      >
        <Icon icon={faLocationDot} style={{ fontSize: 22, opacity: 0.4 }} />
        <span style={{ fontSize: 12 }}>No location data</span>
        <span
          style={{
            fontSize: 11,
            opacity: 0.7,
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
          }}
        >
          Lat: 0 | Lng: 0 | Acc: 0m | Alt: 0m
        </span>
      </div>
    );
  }

  const entry = entries[currentIndex];
  const headerLabel = tabKind === 'located' ? t('locatedAt') : t('lastScanned');
  const altitude = entry.altitude ?? 0;
  const accuracy = entry.accuracy ?? 0;
  // Keep GPS altitude in the details; HERE alt elevates markers above the ground.
  const mapPoint =
    entry.latitude && entry.longitude
      ? { lat: entry.latitude, lng: entry.longitude }
      : undefined;
  const markers = mapPoint ? [mapPoint] : undefined;
  const circle = mapPoint
    ? { center: mapPoint, radius: accuracy || 0 }
    : undefined;
  const showScroll = enableScroll && entries.length > 1;

  return (
    <div className="d-flex flex-column" style={{ height: 300 }}>
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ gap: 8, marginBottom: 8, minHeight: 24 }}
      >
        <div
          className="d-flex align-items-center"
          style={{ gap: 8, minWidth: 0, fontSize: 12 }}
        >
          <Icon icon={faLocationDot} style={{ color: '#041b53' }} />
          <span style={{ fontWeight: 600, color: '#262626' }}>
            {headerLabel}
          </span>
          <span style={{ color: '#bfbfbf' }}>·</span>
          <span style={{ color: '#595959' }}>
            {timeConverter(entry.scanDate)}
            {tabKind === 'lastScan' &&
              ` — ${categoryLabel(t, entry.category, entry.subCategory)}`}
          </span>
        </div>
        {showScroll && (
          <div
            className="d-flex align-items-center"
            style={{ gap: 2, flexShrink: 0 }}
          >
            <Tooltip title="First">
              <Button
                type="text"
                size="small"
                aria-label="first"
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex(0)}
                icon={<Icon icon={faAnglesLeft} />}
              />
            </Tooltip>
            <Tooltip title="Previous">
              <Button
                type="text"
                size="small"
                aria-label="previous"
                disabled={currentIndex === 0}
                onClick={() => setCurrentIndex((i) => Math.max(0, i - 1))}
                icon={<Icon icon={faChevronLeft} />}
              />
            </Tooltip>
            <span
              style={{
                fontSize: 11,
                color: '#595959',
                minWidth: 44,
                textAlign: 'center',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {`${currentIndex + 1} / ${entries.length}`}
            </span>
            <Tooltip title="Next">
              <Button
                type="text"
                size="small"
                aria-label="next"
                disabled={currentIndex === entries.length - 1}
                onClick={() =>
                  setCurrentIndex((i) => Math.min(entries.length - 1, i + 1))
                }
                icon={<Icon icon={faChevronRight} />}
              />
            </Tooltip>
            <Tooltip title="Last">
              <Button
                type="text"
                size="small"
                aria-label="last"
                disabled={currentIndex === entries.length - 1}
                onClick={() => setCurrentIndex(entries.length - 1)}
                icon={<Icon icon={faAnglesRight} />}
              />
            </Tooltip>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: 6,
          marginBottom: 8,
        }}
      >
        <Stat label="LAT" value={mapPoint?.lat ?? 0} />
        <Stat label="LNG" value={mapPoint?.lng ?? 0} />
        <Stat label="ACC" value={`${accuracy} m`} />
        <Stat label="ALT" value={`${altitude} m`} />
      </div>

      <div
        className="flex-fill position-relative"
        style={{
          borderRadius: 8,
          overflow: 'hidden',
          border: '1px solid #f0f0f0',
        }}
      >
        {!mapImageForPdf && (
          <Tooltip title="Fullscreen">
            <Button
              size="small"
              aria-label="expand"
              className="position-absolute"
              style={{
                top: 8,
                right: 8,
                zIndex: 2,
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)',
              }}
              icon={<Icon icon={faExpand} />}
              onClick={() => {
                const view = internalMapRef.current?.getView();
                if (view) {
                  setMapViewData(view);
                  setShowFullMap(true);
                }
              }}
            />
          </Tooltip>
        )}
        {mapImageForPdf ? (
          <img
            src={mapImageForPdf}
            alt="map"
            className="h-100 w-100"
            style={{ objectFit: 'cover' }}
          />
        ) : (
          <Map
            ref={setMapRef}
            markers={markers}
            zoom={14}
            center={mapPoint}
            showSiteOverlays={true}
          />
        )}
      </div>
      <Drawer
        title={categoryLabel(t, entry.category, entry.subCategory)}
        placement="right"
        open={showFullMap}
        onClose={() => setShowFullMap(false)}
        classNames={{ wrapper: 'vw-100', header: 'p-2', body: 'p-2' }}
      >
        <FullscreenMap
          markers={markers}
          circle={circle}
          zoom={mapViewData?.zoom}
          bounds={mapViewData?.bounds}
        />
      </Drawer>
    </div>
  );
};
